// Chat attachments: upload one, take it back, and the browser-side downscale
// that keeps a phone photo from being a 12 MB upload nobody benefits from.

import { getAuthToken } from './api';

const BASE = '/api/v1/files';

// A vision endpoint scales an image down to roughly this on the long edge
// before the model sees it, so anything bigger is bytes the user waits on, we
// store, and the provider then throws away.
const MAX_EDGE = 2048;

// Quality for the re-encode. High enough that text in a screenshot stays
// readable, low enough that the file actually shrinks.
const JPEG_QUALITY = 0.85;

const units = ['B', 'kB', 'MB', 'GB'];

// Shared so a limit reads the same in the chip that reports it and the message
// that refused it.
export function humanSize(bytes) {
  let size = bytes;
  let unit = 0;

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  return `${size < 10 && unit > 0 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
}

export async function uploadFile(conversationID, file, { signal } = {}) {
  const token = await getAuthToken();

  const body = new FormData();
  body.append('file', file, file.name);
  body.append('conversation', conversationID);

  // no Content-Type: the browser sets it with the multipart boundary
  const response = await fetch(BASE, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
    signal,
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return response.json();
}

export async function deleteFile(fileID) {
  const token = await getAuthToken();

  const response = await fetch(`${BASE}/${encodeURIComponent(fileID)}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Already gone is the outcome we wanted, so don't make the caller care.
  if (!response.ok && response.status !== 404) {
    throw new Error(await errorMessage(response));
  }
}

async function errorMessage(response) {
  const body = await response.json().catch(() => null);

  return (
    body?.error?.Message ||
    body?.error?.message ||
    `request failed (${response.status})`
  );
}

// downscaleImage returns a smaller File, or the original when there's nothing
// to gain. It never throws: a format the canvas can't decode (HEIC on a
// browser without support, a corrupt file) just uploads as-is and the size
// limit has the last word.
export async function downscaleImage(file) {
  // An animated GIF loses its animation through a canvas, and SVG is text that
  // rasterising would only make bigger.
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = MAX_EDGE / Math.max(bitmap.width, bitmap.height);

    if (scale >= 1) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return file;

    // A photo that was already well compressed can come out bigger, and then
    // the resize was pointless.
    if (blob.size >= file.size) return file;

    return new File([blob], renameToJPEG(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

// The bytes are JPEG now, so a .png extension would be a lie the download
// header repeats.
function renameToJPEG(name) {
  return `${name.replace(/\.[^./\\]+$/, '')}.jpg`;
}
